import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { GlobalConfigApiService } from '../api/global-config.service';
import { GlobalConfigService } from '../services/global-config.service';
import { ConfigSetting } from '../../pages/admin-welcome/global-config/global-config.model';

/**
 * Resolver that loads global configuration before navigating to a route.
 * Checks for cached config in GlobalConfigService before making API calls.
 * Returns an empty ConfigSetting on error to allow navigation to proceed.
 */
export const globalConfigResolver: ResolveFn<ConfigSetting> = (): Observable<ConfigSetting> => {
  const globalConfigService = inject(GlobalConfigService);
  const globalConfigApiService = inject(GlobalConfigApiService);

  // Always fetch fresh from API to reflect config changes immediately
  globalConfigApiService.resetCache();
  return globalConfigApiService.getConfiguration().pipe(
    map((result) => {
      // Handle case where result or result.response might be undefined
      const configData = result?.response || {};
      return new ConfigSetting(configData);
    }),
    tap((config) => {
      globalConfigService.updateGlobalConfig(config);
    }),
    catchError((error) => {
      console.error('Failed to load global configuration:', error);
      const emptyConfig = new ConfigSetting({});
      globalConfigService.updateGlobalConfig(emptyConfig);
      return of(emptyConfig);
    })
  );
};
